import * as THREE from 'three'
import type { SceneManager } from './SceneManager'
import type { SceneObject } from '../types/scene'
import { collectPipePortsWorld } from './pipePorts'

const SNAP_THRESHOLD = 3
const INDICATOR_COLOR = 0x00cc44

/** 管道端口吸附：拖拽时显示绿色提示圆，松手时自动对齐 */
export class PipeSnapHelper {
  private _sm: SceneManager
  private _indicators: THREE.Mesh[] = []
  private _indicatorGroup = new THREE.Group()

  constructor(sm: SceneManager) {
    this._sm = sm
    this._indicatorGroup.name = '__pipe_snap_indicators__'
    sm.scene.add(this._indicatorGroup)
  }

  /** 获取场景中所有管道端口的世界坐标（排除指定对象） */
  private _getAllPorts(objects: SceneObject[], excludeId?: string): { pos: THREE.Vector3; dir: THREE.Vector3 }[] {
    const result: { pos: THREE.Vector3; dir: THREE.Vector3 }[] = []
    for (const obj of objects) {
      if (obj.id === excludeId || obj.type !== 'pipe_polyline') continue
      const ox = Number(obj.position?.[0] ?? 0)
      const oy = Number(obj.position?.[1] ?? 0)
      const oz = Number(obj.position?.[2] ?? 0)
      const append = (list: any[]) => {
        for (const p of list) {
          const x = Number((p as any)?.x)
          const y = Number((p as any)?.y)
          const z = Number((p as any)?.z)
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue
          result.push({
            pos: new THREE.Vector3(x + ox, y + oy, z + oz),
            dir: new THREE.Vector3(0, 1, 0),
          })
        }
      }
      if (Array.isArray(obj.params?.waypoints)) append(obj.params.waypoints)
      if (Array.isArray(obj.params?.topologyNodes)) append(obj.params.topologyNodes)
    }
    for (const p of collectPipePortsWorld(objects, excludeId)) {
      result.push({ pos: p.position, dir: p.direction })
    }
    return result
  }

  /** 检查 (x,z) 是否靠近端口，返回最近可吸附的端口位置 */
  snapPosition(x: number, z: number, objects: SceneObject[], excludeId?: string): { x: number; z: number } | null {
    const ports = this._getAllPorts(objects, excludeId)
    let bestDist = SNAP_THRESHOLD
    let best: { x: number; z: number } | null = null

    for (const { pos } of ports) {
      const dx = x - pos.x
      const dz = z - pos.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist < bestDist) {
        bestDist = dist
        best = { x: pos.x, z: pos.z }
      }
    }
    return best
  }

  /** 更新绿色提示圆：显示拖拽位置附近的端口 */
  updateIndicators(x: number, z: number, objects: SceneObject[], excludeId?: string) {
    this._clearIndicators()
    const ports = this._getAllPorts(objects, excludeId)

    for (const { pos } of ports) {
      const dx = x - pos.x
      const dz = z - pos.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist < SNAP_THRESHOLD) {
        const mesh = new THREE.Mesh(
          new THREE.RingGeometry(0.8, 1.2, 16),
          new THREE.MeshBasicMaterial({
            color: INDICATOR_COLOR,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.8,
            depthTest: false
          })
        )
        mesh.position.set(pos.x, pos.y + 0.02, pos.z)
        mesh.rotation.x = -Math.PI / 2
        this._indicators.push(mesh)
        this._indicatorGroup.add(mesh)
      }
    }
    this._sm.markDirty()
  }

  private _clearIndicators() {
    for (const m of this._indicators) {
      this._indicatorGroup.remove(m)
      m.geometry.dispose()
      ;(m.material as THREE.Material).dispose()
    }
    this._indicators = []
  }

  clear() {
    this._clearIndicators()
  }

  dispose() {
    this.clear()
    this._sm.scene.remove(this._indicatorGroup)
  }
}
